/**
 * Centralized team/league logo resolver + alias mapping for the sportsbook.
 *
 * Official logos are served from ESPN's public team-logo CDN, resolved by
 * league + team abbreviation. Any name that cannot be resolved (unknown team,
 * college matchup, or a non-team market outcome like "Over 45.5") returns null,
 * and the TeamLogo component renders a premium black-and-gold initials badge.
 * A broken/missing image also falls back to initials via onError, so a logo can
 * never appear broken.
 */

export const LOGO_PACK_ENABLED = true;

const ESPN_BASE = 'https://a.espncdn.com/i/teamlogos';
const ESPN_LEAGUE = 'https://a.espncdn.com/combiner/i?img=/i/teamlogos/leagues/500';

// sport_key (from The Odds API) -> ESPN league dir + display label + generic glyph.
export const LEAGUE_META = {
    americanfootball_nfl: { dir: 'nfl', label: 'NFL', glyph: '🏈' },
    americanfootball_ncaaf: { dir: 'ncaaf', label: 'NCAAF', glyph: '🏈' },
    basketball_nba: { dir: 'nba', label: 'NBA', glyph: '🏀' },
    basketball_wnba: { dir: 'wnba', label: 'WNBA', glyph: '🏀' },
    basketball_ncaab: { dir: 'mens-college-basketball', label: 'NCAAB', glyph: '🏀' },
    baseball_mlb: { dir: 'mlb', label: 'MLB', glyph: '⚾' },
    icehockey_nhl: { dir: 'nhl', label: 'NHL', glyph: '🏒' },
    boxing_boxing: { dir: 'boxing', label: 'Boxing', glyph: '🥊' },
    mma_mixed_martial_arts: { dir: 'mma', label: 'MMA', glyph: '🥋' },
    soccer_epl: { dir: 'soccer', label: 'EPL', glyph: '⚽' },
    soccer_spain_la_liga: { dir: 'soccer', label: 'La Liga', glyph: '⚽' },
    soccer_italy_serie_a: { dir: 'soccer', label: 'Serie A', glyph: '⚽' },
    soccer_germany_bundesliga: { dir: 'soccer', label: 'Bundesliga', glyph: '⚽' },
    soccer_usa_mls: { dir: 'soccer', label: 'MLS', glyph: '⚽' },
    soccer_uefa_champs_league: { dir: 'soccer', label: 'UCL', glyph: '⚽' },
};

export const leagueGlyph = (sportKey) => LEAGUE_META[sportKey]?.glyph || '🏆';
export const leagueLabel = (sportKey) => LEAGUE_META[sportKey]?.label || '';

const stripAccents = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Normalize a team name for map lookup: lowercase, drop accents/periods, collapse spaces.
const normName = (name = '') =>
    stripAccents(String(name).toLowerCase())
        .replace(/\./g, '')
        .replace(/\s+/g, ' ')
        .trim();

export const slugTeam = (name = '') =>
    stripAccents(String(name).toLowerCase())
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

// Per-league  normalized full team name -> ESPN abbreviation.
export const TEAM_ABBR = {
    nfl: {
        'arizona cardinals': 'ari', 'atlanta falcons': 'atl', 'baltimore ravens': 'bal',
        'buffalo bills': 'buf', 'carolina panthers': 'car', 'chicago bears': 'chi',
        'cincinnati bengals': 'cin', 'cleveland browns': 'cle', 'dallas cowboys': 'dal',
        'denver broncos': 'den', 'detroit lions': 'det', 'green bay packers': 'gb',
        'houston texans': 'hou', 'indianapolis colts': 'ind', 'jacksonville jaguars': 'jax',
        'kansas city chiefs': 'kc', 'las vegas raiders': 'lv', 'los angeles chargers': 'lac',
        'los angeles rams': 'lar', 'miami dolphins': 'mia', 'minnesota vikings': 'min',
        'new england patriots': 'ne', 'new orleans saints': 'no', 'new york giants': 'nyg',
        'new york jets': 'nyj', 'philadelphia eagles': 'phi', 'pittsburgh steelers': 'pit',
        'san francisco 49ers': 'sf', 'seattle seahawks': 'sea', 'tampa bay buccaneers': 'tb',
        'tennessee titans': 'ten', 'washington commanders': 'wsh',
    },
    nba: {
        'atlanta hawks': 'atl', 'boston celtics': 'bos', 'brooklyn nets': 'bkn',
        'charlotte hornets': 'cha', 'chicago bulls': 'chi', 'cleveland cavaliers': 'cle',
        'dallas mavericks': 'dal', 'denver nuggets': 'den', 'detroit pistons': 'det',
        'golden state warriors': 'gs', 'houston rockets': 'hou', 'indiana pacers': 'ind',
        'la clippers': 'lac', 'los angeles clippers': 'lac', 'los angeles lakers': 'lal',
        'memphis grizzlies': 'mem', 'miami heat': 'mia', 'milwaukee bucks': 'mil',
        'minnesota timberwolves': 'min', 'new orleans pelicans': 'no', 'new york knicks': 'ny',
        'oklahoma city thunder': 'okc', 'orlando magic': 'orl', 'philadelphia 76ers': 'phi',
        'phoenix suns': 'phx', 'portland trail blazers': 'por', 'sacramento kings': 'sac',
        'san antonio spurs': 'sa', 'toronto raptors': 'tor', 'utah jazz': 'utah',
        'washington wizards': 'wsh',
    },
    wnba: {
        'atlanta dream': 'atl', 'chicago sky': 'chi', 'connecticut sun': 'conn',
        'dallas wings': 'dal', 'golden state valkyries': 'gs', 'indiana fever': 'ind',
        'las vegas aces': 'lv', 'los angeles sparks': 'la', 'minnesota lynx': 'min',
        'new york liberty': 'ny', 'phoenix mercury': 'phx', 'seattle storm': 'sea',
        'washington mystics': 'wsh',
    },
    mlb: {
        'arizona diamondbacks': 'ari', 'atlanta braves': 'atl', 'baltimore orioles': 'bal',
        'boston red sox': 'bos', 'chicago cubs': 'chc', 'chicago white sox': 'chw',
        'cincinnati reds': 'cin', 'cleveland guardians': 'cle', 'colorado rockies': 'col',
        'detroit tigers': 'det', 'houston astros': 'hou', 'kansas city royals': 'kc',
        'los angeles angels': 'laa', 'los angeles dodgers': 'lad', 'miami marlins': 'mia',
        'milwaukee brewers': 'mil', 'minnesota twins': 'min', 'new york mets': 'nym',
        'new york yankees': 'nyy', 'oakland athletics': 'oak', athletics: 'oak',
        'philadelphia phillies': 'phi', 'pittsburgh pirates': 'pit', 'san diego padres': 'sd',
        'san francisco giants': 'sf', 'seattle mariners': 'sea', 'st louis cardinals': 'stl',
        'tampa bay rays': 'tb', 'texas rangers': 'tex', 'toronto blue jays': 'tor',
        'washington nationals': 'wsh',
    },
    nhl: {
        'anaheim ducks': 'ana', 'boston bruins': 'bos', 'buffalo sabres': 'buf',
        'calgary flames': 'cgy', 'carolina hurricanes': 'car', 'chicago blackhawks': 'chi',
        'colorado avalanche': 'col', 'columbus blue jackets': 'cbj', 'dallas stars': 'dal',
        'detroit red wings': 'det', 'edmonton oilers': 'edm', 'florida panthers': 'fla',
        'los angeles kings': 'la', 'minnesota wild': 'min', 'montreal canadiens': 'mtl',
        'nashville predators': 'nsh', 'new jersey devils': 'nj', 'new york islanders': 'nyi',
        'new york rangers': 'nyr', 'ottawa senators': 'ott', 'philadelphia flyers': 'phi',
        'pittsburgh penguins': 'pit', 'san jose sharks': 'sj', 'seattle kraken': 'sea',
        'st louis blues': 'stl', 'tampa bay lightning': 'tb', 'toronto maple leafs': 'tor',
        'vancouver canucks': 'van', 'vegas golden knights': 'vgk', 'washington capitals': 'wsh',
        'winnipeg jets': 'wpg', 'utah hockey club': 'utah', 'utah mammoth': 'utah',
    },
};

// Soccer clubs use ESPN numeric ids under /soccer/500/{id}.png. Unmapped clubs
// fall back to initials; a wrong id also safely falls back (onError). All ids
// below verified against the ESPN CDN.
export const SOCCER_IDS = {
    // Premier League
    'manchester united': 360, 'manchester city': 382, 'liverpool': 364, 'arsenal': 359,
    'chelsea': 363, 'tottenham hotspur': 367, tottenham: 367, 'newcastle united': 361,
    'aston villa': 362, 'west ham united': 371, everton: 368, 'brighton and hove albion': 331,
    brighton: 331, 'crystal palace': 384, fulham: 370, brentford: 337,
    'wolverhampton wanderers': 380, wolves: 380, 'nottingham forest': 393,
    'afc bournemouth': 349, bournemouth: 349, 'leicester city': 375, leicester: 375,
    'leeds united': 357, southampton: 376, 'ipswich town': 373,
    // La Liga
    'real madrid': 86, barcelona: 83, 'atletico madrid': 1068, sevilla: 243,
    'athletic bilbao': 93, 'athletic club': 93, 'real sociedad': 89, 'real betis': 244,
    villarreal: 102, valencia: 94, girona: 9812, osasuna: 85, 'celta vigo': 85, getafe: 2922,
    // Serie A
    juventus: 111, 'inter milan': 110, 'ac milan': 103, napoli: 114, 'as roma': 104, roma: 104,
    lazio: 112, atalanta: 105, fiorentina: 109, bologna: 107, torino: 539,
    // Bundesliga
    'bayern munich': 132, 'borussia dortmund': 124, 'rb leipzig': 11420,
    'bayer leverkusen': 131, 'eintracht frankfurt': 125, 'vfb stuttgart': 134,
    'vfl wolfsburg': 138, wolfsburg: 138, 'borussia monchengladbach': 268,
    // Ligue 1
    'paris saint germain': 160, psg: 160, 'olympique marseille': 176, marseille: 176,
    'as monaco': 174, monaco: 174, 'olympique lyonnais': 167, lyon: 167,
    // MLS
    'la galaxy': 187, 'los angeles fc': 18966, lafc: 18966, 'inter miami cf': 20232,
    'inter miami': 20232, 'seattle sounders fc': 9726, 'seattle sounders': 9726,
    'atlanta united fc': 18418, 'atlanta united': 18418, 'austin fc': 20906,
    'portland timbers': 9723, 'new york city fc': 17606, nycfc: 17606,
    'new york red bulls': 190, 'columbus crew': 183, 'fc cincinnati': 18267,
};

// NCAA schools use ESPN ids under /ncaa/500/{id}.png (same id for football &
// basketball). Matched by longest school-name substring of the team name so
// "Michigan State" beats "Michigan". Unmatched schools fall back to initials.
export const NCAA_IDS = {
    alabama: 333, georgia: 61, 'ohio state': 194, michigan: 130, 'michigan state': 127,
    texas: 251, 'texas a&m': 245, 'texas tech': 2641, oklahoma: 201, 'oklahoma state': 197,
    lsu: 99, 'notre dame': 87, usc: 30, ucla: 26, clemson: 228, florida: 57,
    'florida state': 52, 'penn state': 213, oregon: 2483, 'oregon state': 204,
    tennessee: 2633, auburn: 2, wisconsin: 275, nebraska: 158, miami: 2390,
    washington: 264, 'washington state': 265, iowa: 2294, 'iowa state': 66,
    'ole miss': 145, mississippi: 145, 'mississippi state': 344, missouri: 142,
    kentucky: 96, arkansas: 8, 'south carolina': 2579, utah: 254, tcu: 2628,
    baylor: 239, kansas: 2305, 'kansas state': 2306, louisville: 97,
    'north carolina': 153, 'nc state': 152, 'north carolina state': 152, duke: 150,
    virginia: 258, 'virginia tech': 259, colorado: 38, arizona: 12, 'arizona state': 9,
    stanford: 24, california: 25, byu: 252, cincinnati: 2132, ucf: 2116, houston: 248,
    'west virginia': 277, gonzaga: 2250, villanova: 222, uconn: 41, connecticut: 41,
    purdue: 2509, indiana: 84, marquette: 269, creighton: 156, xavier: 2752, illinois: 356,
};

// Resolve a stable key (abbr) from provider id > abbr > per-league map > null.
export const resolveTeamKey = ({ sportKey, teamId, abbr, name } = {}) => {
    if (abbr) return String(abbr).toLowerCase();
    const dir = LEAGUE_META[sportKey]?.dir;
    const n = normName(name);
    if (dir && TEAM_ABBR[dir] && TEAM_ABBR[dir][n]) return TEAM_ABBR[dir][n];
    return null;
};

// Full official logo URL, or null (=> render initials).
export const resolveTeamLogoUrl = ({ sportKey, teamId, abbr, name } = {}) => {
    if (!LOGO_PACK_ENABLED) return null;
    const dir = LEAGUE_META[sportKey]?.dir;
    if (!dir) return null;

    if (dir === 'soccer') {
        const id = SOCCER_IDS[normName(name)];
        return id ? `${ESPN_BASE}/soccer/500/${id}.png` : null;
    }

    // College football / basketball: school-name matching is too unreliable
    // (ambiguous names -> wrong crests), so always fall back to initials.
    if (dir === 'ncaaf' || dir === 'mens-college-basketball') {
        return null;
    }

    const key = resolveTeamKey({ sportKey, teamId, abbr, name });
    if (!key) return null;
    return `${ESPN_BASE}/${dir}/500/${key}.png`;
};

export const leagueLogoUrl = (sportKey) => {
    const dir = LEAGUE_META[sportKey]?.dir;
    const HAS_LEAGUE_LOGO = new Set(['nfl', 'nba', 'mlb', 'nhl', 'wnba']);
    if (!dir || !HAS_LEAGUE_LOGO.has(dir)) return null;
    return `${ESPN_LEAGUE}/${dir}.png`;
};

// Deterministic initials (max 2 chars) from a team/competitor name.
export const teamInitials = (name = '') => {
    const words = String(name).trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};
